import type { ReactNode } from 'react';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { removeInquiryFile } from '@/api/inquiries';
import { useLlmConfig } from '@/api/llmConfig';
import { ACTIVE_RUN_STATUSES, type ComparisonRequest } from '@/api/runs';
import { usePreferences } from '@/features/preferences';
import { useAnnounce } from '@/features/shell/LiveRegion';
import { useBreakpoints } from '@/features/shell/useViewport';
import type { Inquiry } from '@/types/chat/chat';
import { onOpenSource } from '../answer/sourceEvents';
import AttachmentList from '../composer/AttachmentList';
import { blockingReason, type BlockAction } from '../composer/blocking';
import Composer from '../composer/Composer';
import {
  EMPTY_CHOICE,
  comparisonRequest,
  frozenComparison,
  roleCandidates,
  type ComparisonChoice,
} from '../composer/comparisonRoles';
import ComparisonSetup, {
  type ComparisonSetupHandle,
} from '../composer/ComparisonSetup';
import { comparisonQuota } from '../composer/quota';
import SourceChips from '../composer/SourceChips';
import { useAttachmentUploads } from '../composer/useAttachmentUploads';
import { useDraft } from '../composer/useDraft';
import { useSourceChips } from '../composer/useSourceChips';
import ConversationView, {
  type ConversationHandle,
} from '../conversation/ConversationView';
import type { AnswerView } from '../conversation/answerState';
import { MODE_KEYS } from '../conversation/ModeBadge';
import { useConversation } from '../conversation/useConversation';
import { buildCopyPayload } from '../copy/clipboardPayload';
import CopyFallbackDialog from '../copy/CopyFallbackDialog';
import { writeRichClipboard } from '../copy/writeClipboard';
import type { ExportRequest } from '../export/ExportDialog';
import {
  useHandleChatMode,
  type chatModeType,
} from '../hooks/useHandleChatThinkingMode';
import { useRun, useRunStore } from '../runs/runStore';
import type { ConvMessage } from '../types';
import ChatToolbar from './ChatToolbar';
import ChatWindowModals from './ChatWindowModals';
import { useFileStatus } from './hooks/useFileStatus';
import { useInquiry } from './hooks/useInquiry';
import { usePendingRecheck } from './hooks/usePendingRecheck';
import { useRemovedFiles } from './hooks/useRemovedFiles';
import { useResubmissionGuard } from './hooks/useResubmissionGuard';
import { comparisonFromRecord, useRunActions } from './hooks/useRunActions';
import { isSameFileId, removeFileFromInquiry } from './removeFileFromInquiry';
import styles from './chatWindow.module.css';

// The source dialog loads on first open, keeping its table out of the
// initial bundle; once loaded it stays mounted, one per Q&A.
const SourceSelectionDialog = lazy(
  () => import('../selection/SourceSelectionDialog'),
);
// The same for the source panel (with the document viewer) and the prompt
// dialog: neither is needed before its first use.
const SourcePanel = lazy(() => import('../sources/SourcePanel'));
const PromptDialog = lazy(() => import('../prompt/PromptDialog'));

interface ChatWindowProps {
  selectedInquiryId: string | undefined;
  /** Toggled by the Guideline・Context tab after its data changed. */
  reloadData: boolean;
  /** A question was accepted in this Q&A (it moves to the top). */
  onMessageSent?: (inquiryId: string) => void;
  /** Recent Q&As, New Q&A and the title, at the start of the toolbar. */
  toolbarLeading?: ReactNode;
  /** Open the CSV export dialog. */
  onExport: (request: ExportRequest) => void;
}

interface OpenSource {
  messageId: string | number | null;
  runId: string | null;
  n: number;
  opener: HTMLElement | null;
}

/** The Q&A pane of the selected inquiry. */
const ChatWindow = ({
  selectedInquiryId,
  reloadData,
  onMessageSent,
  toolbarLeading,
  onExport,
}: ChatWindowProps) => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const { prefs } = usePreferences();
  const { compact, sourceModal } = useBreakpoints();
  const config = useLlmConfig();
  const announce = useAnnounce();
  const [reloadInquiry, setReloadInquiry] = useState<boolean>(false);
  const [promptOpen, setPromptOpen] = useState<boolean>(false);
  const [promptMounted, setPromptMounted] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [sourcesMounted, setSourcesMounted] = useState(false);
  const [removingFileId, setRemovingFileId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [choices, setChoices] = useState<Record<string, ComparisonChoice>>({});
  const [copyText, setCopyText] = useState<string | null>(null);
  const [openSource, setOpenSource] = useState<OpenSource | null>(null);
  const view = useRef<ConversationHandle>(null);
  const host = useRef<HTMLDivElement>(null);
  const transcript = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const setup = useRef<ComparisonSetupHandle>(null);
  const { chatMode, applyMode, getPersistedMode, handleSetChatMode } =
    useHandleChatMode({ defaultChatMode: 'standard' });
  const removedFileIdsFor = useRemovedFiles();
  const draft = useDraft(selectedInquiryId);

  const {
    inquiry,
    setInquiry,
    firstLoad,
    loading: inquiryLoading,
    failed: inquiryFailed,
    openedId,
    isCurrentInquiry,
    refreshInquiry,
  } = useInquiry({
    inquiryId: selectedInquiryId,
    reloadData,
    reloadInquiry,
    removedFileIdsFor,
    onOpened: (inquiryId, stored: Inquiry) => {
      // A mode chosen in this browser wins over the stored one.
      const persisted = getPersistedMode(inquiryId);
      applyMode(
        persisted ?? ((stored.chat_mode || 'standard') as chatModeType),
      );
    },
  });

  const fileStatus = useFileStatus({
    inquiryId: selectedInquiryId,
    reloadData,
    reloadInquiry,
    removedFileIdsFor,
    refreshInquiry,
  });

  const reload = useCallback(() => setReloadInquiry(value => !value), []);
  // Until the selected Q&A has loaded, the previous one's data must not be
  // shown, counted or written into it.
  // An object from an earlier visit of the same Q&A does not count either:
  // only data loaded since it was selected, with its mode applied.
  const current =
    inquiry &&
    String(inquiry.id) === String(selectedInquiryId) &&
    openedId === String(selectedInquiryId)
      ? inquiry
      : undefined;

  const uploads = useAttachmentUploads({
    inquiryId: selectedInquiryId,
    serverNames: fileStatus.files.map(file => file.name),
    serverFileIds: fileStatus.files.map(file => String(file.id)),
    serverReady: !fileStatus.loading,
    onUploaded: inquiryId => {
      if (isCurrentInquiry(inquiryId)) reload();
    },
  });

  const chips = useSourceChips({
    inquiryId: selectedInquiryId,
    inquiry: current,
    onChanged: reload,
  });

  const conversation = useConversation(selectedInquiryId);
  const runs = useRunStore();
  const entry = runs.get(selectedInquiryId);
  // A run the list says is active but that is not followed yet (its
  // pick-up is on its way or failed): the Q&A is busy all the same.
  const listedRunId =
    conversation.messages.find(
      item =>
        !!item.run_id &&
        !item.local &&
        ACTIVE_RUN_STATUSES.has(String(item.run_status)) &&
        !runs.getRun(item.run_id),
    )?.run_id ?? null;
  const busy = entry.generating || !!listedRunId;
  const busyRunId = entry.runId ?? listedRunId;
  const activeRun = useRun(entry.runId);

  // Roles belong to one Q&A's selection; the panel to one Q&A's answers.
  useEffect(() => setOpenSource(null), [selectedInquiryId]);

  // The mode chosen in this browser for the newly selected Q&A applies at
  // once; the stored one follows when it has loaded (onOpened).
  useEffect(() => {
    if (!selectedInquiryId) return;
    const persisted = getPersistedMode(selectedInquiryId);
    if (persisted) applyMode(persisted);
    // applyMode and getPersistedMode are stable in effect (state setter,
    // module function).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInquiryId]);

  useEffect(
    () =>
      onOpenSource(detail => {
        if (detail.citation == null) return;
        setOpenSource({
          messageId: detail.messageId,
          runId: detail.runId,
          n: Number(detail.n),
          opener: detail.opener ?? null,
        });
      }),
    [],
  );

  const onSent = useCallback(
    (inquiryId: string) => {
      onMessageSent?.(inquiryId);
      requestAnimationFrame(() => view.current?.jumpToLatest());
    },
    [onMessageSent],
  );

  const totals = current?.selection_totals;

  usePendingRecheck({
    inquiryId: selectedInquiryId,
    pendingCount: Number(totals?.pending_count ?? 0),
    refresh: id => void refreshInquiry(id, () => true),
  });
  const choice =
    (selectedInquiryId && choices[selectedInquiryId]) || EMPTY_CHOICE;
  const setChoice = (next: ComparisonChoice) => {
    if (!selectedInquiryId) return;
    setChoices(previous => ({ ...previous, [selectedInquiryId]: next }));
  };
  const candidates = roleCandidates(totals);
  const chosenRoles = comparisonRequest(choice, candidates);
  const quota =
    chatMode === 'comparison' && totals
      ? comparisonQuota(totals, config.modes.comparison, config.bytes_per_mb)
      : null;
  const nameOf = (id: number | undefined) =>
    candidates.find(file => Number(file.id) === id)?.name ?? `#${id}`;

  const block = selectedInquiryId
    ? blockingReason({
        mode: chatMode,
        runActive: busy,
        // Loaded for this selection with its mode applied, and the latest
        // load succeeded (a failed reload leaves totals from before a change).
        inquiryReady: !!current && !inquiryFailed,
        loadFailed: inquiryFailed,
        totals,
        // Also while the totals are read again after a change: the old
        // numbers may no longer hold.
        calculating: firstLoad || inquiryLoading,
        uploading: uploads.uploading,
        limits: config.modes.comparison,
        roles: chatMode === 'comparison' ? chosenRoles : null,
      })
    : null;

  /** Composer roles first; else the question's own, if still selected. */
  const editComparison = (item: ConvMessage): ComparisonRequest | undefined => {
    if (chosenRoles.ok) return chosenRoles.request;
    const recorded = comparisonFromRecord(item.comparison);
    if (!recorded) return undefined;
    const roleId =
      recorded.purpose === 'changes'
        ? recorded.baseline_file_id
        : recorded.document_to_check_id;
    return candidates.some(file => Number(file.id) === roleId)
      ? recorded
      : undefined;
  };

  const actions = useRunActions({
    inquiryId: selectedInquiryId,
    mode: chatMode,
    editComparison,
    freezeComparison: request => frozenComparison(request, candidates),
    addPending: conversation.addPending,
    reload: conversation.reload,
    onSent,
    messageApi,
    runIds: () =>
      conversation.messages.flatMap(item => (item.run_id ? [item.run_id] : [])),
  });

  const {
    retry: guardedRetry,
    alternative: guardedAlternative,
    resubmitEdited: guardedResubmit,
  } = useResubmissionGuard({
    ready: !!current,
    failed: inquiryFailed,
    messageApi,
    retry: actions.retry,
    alternative: actions.alternative,
    resubmitEdited: actions.resubmitEdited,
  });

  const send = async ({ readyOnly }: { readyOnly: boolean }) => {
    const inquiryId = selectedInquiryId;
    const text = draft.text;
    if (!inquiryId || !text.trim() || sending || busy) return;
    if (block && !(readyOnly && block.actions.includes('send_ready_only'))) {
      return;
    }
    let comparison: ComparisonRequest | undefined;
    if (chatMode === 'comparison') {
      if (!chosenRoles.ok) return;
      comparison = chosenRoles.request;
    }
    setSending(true);
    const clientSentAt = draft.prepareSend(inquiryId, text);
    const sent = await actions.send(text, comparison, {
      sendReadyOnly: readyOnly,
      clientSentAt,
      frozen: comparison ? frozenComparison(comparison, candidates) : undefined,
    });
    setSending(false);
    if (sent) draft.sent(inquiryId, text);
    else draft.resume(inquiryId);
  };

  const openSources = () => {
    setSourcesMounted(true);
    setSourcesOpen(true);
  };

  const onBlockAction = (action: BlockAction) => {
    switch (action) {
      case 'stop':
        if (busyRunId) actions.stop(busyRunId);
        break;
      case 'select_sources':
        openSources();
        break;
      case 'switch_chat':
        handleSetChatMode('chat_only', selectedInquiryId);
        break;
      case 'send_ready_only':
        void send({ readyOnly: true });
        break;
      case 'show_breakdown':
        setup.current?.showBreakdown();
        break;
      case 'choose_role':
        setup.current?.focusRole();
        break;
      case 'retry_load':
        reload();
        break;
    }
  };

  const removeFile = async (fileId: string) => {
    if (!selectedInquiryId) return;

    const inquiryIdAtCallTime = selectedInquiryId;
    setRemovingFileId(fileId);
    const response = await removeInquiryFile(inquiryIdAtCallTime, fileId);
    setRemovingFileId(null);

    if (!response.success) {
      if (isCurrentInquiry(inquiryIdAtCallTime)) {
        messageApi.error(response.error || t('home.chat.files.removeError'));
      }
      return;
    }

    removedFileIdsFor(inquiryIdAtCallTime).add(String(fileId));

    if (!isCurrentInquiry(inquiryIdAtCallTime)) {
      return;
    }

    fileStatus.setFiles(prev =>
      prev.filter(file => !isSameFileId(file.id, fileId)),
    );
    setInquiry((prev: any) => removeFileFromInquiry(prev, fileId));
    announce(t('home.chat.files.removeSuccess'));
    // The totals change without it.
    reload();
  };

  /** Copy with formatting; the manual dialog when the browser refuses. */
  // Stable, so typing does not re-render every (memoized) answer.
  const copyAnswer = useCallback(
    (
      _item: ConvMessage,
      answer: AnswerView,
      table: number | null,
    ): Promise<boolean> => {
      const payload = buildCopyPayload({
        answer: answer.text,
        legacy: answer.legacy,
        partial: answer.partial,
        citations: answer.citations,
        scope:
          table == null ? { kind: 'answer' } : { kind: 'table', index: table },
        labels: {
          sources: t('v2.messages.sources'),
          unnamed: t('v2.sources.unavailable'),
        },
      });
      if (!payload) return Promise.resolve(false);
      // Straight from the click: Safari allows clipboard writes only then.
      return writeRichClipboard(payload).then(ok => {
        if (ok) {
          announce(
            table == null
              ? t('v2.messages.copiedAnswer')
              : t('v2.messages.copiedTable'),
          );
        } else {
          setCopyText(payload.text);
        }
        return ok;
      });
    },
    [announce, t],
  );

  const exportTarget = useRef({
    onExport,
    inquiryId: selectedInquiryId,
    title: current?.name,
  });
  exportTarget.current = {
    onExport,
    inquiryId: selectedInquiryId,
    title: current?.name,
  };
  const exportAnswer = useCallback(
    (item: ConvMessage, comparisonTable: boolean) => {
      const target = exportTarget.current;
      if (!target.inquiryId) return;
      target.onExport({
        inquiryId: target.inquiryId,
        messageId: item.id,
        comparisonTable,
        title: target.title,
      });
    },
    [],
  );

  /** What an edited resubmission is sent with, comparison roles included. */
  const editConditions = (item: ConvMessage) => {
    const mode = (item.mode ?? chatMode) as chatModeType;
    const text = t('v2.messages.editConditions', {
      mode: t(MODE_KEYS[mode] ?? MODE_KEYS.standard),
    });
    if (mode !== 'comparison') return text;
    const roles = editComparison(item);
    if (!roles) return `${text} ${t('v2.composer.rBaseline')}`;
    const role =
      roles.purpose === 'changes'
        ? t('v2.common.labelValue', {
            label: t('v2.composer.cmpBaseline'),
            value: nameOf(roles.baseline_file_id),
          })
        : t('v2.common.labelValue', {
            label: t('v2.comparison.cmpDocToCheck'),
            value: nameOf(roles.document_to_check_id),
          });
    const purpose =
      roles.purpose === 'changes'
        ? t('v2.composer.cmpVersion')
        : t('v2.composer.cmpRefCheck');
    return `${text} ${purpose} · ${role}`;
  };

  const sourceMessage = openSource
    ? conversation.messages.find(
        item => String(item.id) === String(openSource.messageId),
      )
    : undefined;
  const sourceRun = useRun(openSource?.runId ?? null);
  const sourceCitations =
    sourceRun?.citations ?? sourceMessage?.citations ?? null;
  const sourceRequest =
    openSource && sourceCitations?.length
      ? {
          answerKey: String(openSource.messageId ?? openSource.runId),
          citations: sourceCitations,
          n: openSource.n,
        }
      : null;
  const attachmentsForSources = (fileStatus.files ?? []).map(file => ({
    id: file.id,
    name: file.name,
  }));

  const comparisonLimits = config.modes.comparison;
  const emptyModeKey = {
    standard: 'empty_normal',
    thinking: 'empty_deep',
    comparison: 'empty_compare',
    chat_only: 'empty_chat',
  }[chatMode];

  const panel = (
    <SourcePanel
      request={sourceRequest}
      docked={!sourceModal}
      opener={openSource?.opener ?? null}
      attachments={attachmentsForSources}
      onNavigate={n =>
        setOpenSource(current => (current ? { ...current, n } : current))
      }
      onClose={() => setOpenSource(null)}
      onCopyFailed={setCopyText}
    />
  );

  // An empty Q&A centres its guidance and the composer together (kit); the
  // composer moves to the bottom as soon as the first question exists.
  const isEmpty =
    conversation.loaded &&
    !conversation.failed &&
    conversation.messages.length === 0;

  return (
    <div ref={host} className={styles.window}>
      <div className={styles.main} data-empty={isEmpty || undefined}>
        <ChatToolbar
          leading={toolbarLeading}
          hasInquiry={!!selectedInquiryId}
          canExport={!isEmpty}
          onExportCsv={() =>
            selectedInquiryId &&
            onExport({ inquiryId: selectedInquiryId, title: current?.name })
          }
          onEditPrompt={() => {
            setPromptMounted(true);
            setPromptOpen(true);
          }}
        />
        <div ref={transcript} className={styles.transcript}>
          <ConversationView
            ref={view}
            inquiryId={selectedInquiryId}
            messages={conversation.messages}
            loaded={conversation.loaded}
            failed={conversation.failed}
            onRetryLoad={() => void conversation.reload()}
            links={conversation.links}
            busy={busy}
            followLatest={prefs.follow_latest}
            empty={{
              title: t('v2.composer.emptyAsk'),
              body: t(`v2.composer.${emptyModeKey}`, {
                min: comparisonLimits.min_files,
                maxFiles: comparisonLimits.max_files,
                maxMb: comparisonQuota(
                  { file_count: 0, total_size_mb: 0, total_bytes: 0 },
                  comparisonLimits,
                  config.bytes_per_mb,
                ).maxMb,
              }),
            }}
            editConditions={editConditions}
            unrecordedModeNote={t('v2.messages.modeNotRecordedSend', {
              mode: t(MODE_KEYS[chatMode]),
            })}
            onStop={actions.stop}
            onReconnect={actions.reconnect}
            onRetry={guardedRetry}
            onAlternative={guardedAlternative}
            onResubmit={guardedResubmit}
            onCopyFailed={setCopyText}
            onCopyAnswer={copyAnswer}
            onExportAnswer={exportAnswer}
          />
        </div>
        <Composer
          inquiryId={selectedInquiryId}
          value={draft.text}
          onChange={draft.setText}
          onSend={options => void send(options)}
          mode={chatMode}
          onModeChange={mode => handleSetChatMode(mode, selectedInquiryId)}
          sendKey={prefs.send_key}
          compact={compact}
          sending={sending}
          generating={busy && !sending}
          // Until the run id is known (POST on its way) Stop has nothing
          // to cancel; after a failed cancel it can be pressed again.
          canStop={
            !!busyRunId &&
            (!activeRun ||
              activeRun.stop === 'none' ||
              activeRun.stop === 'failed')
          }
          stopRequested={
            !!activeRun &&
            (activeRun.stop === 'requested' || activeRun.stop === 'unconfirmed')
          }
          onStop={() => busyRunId && actions.stop(busyRunId)}
          block={block}
          onBlockAction={onBlockAction}
          onOpenSources={openSources}
          onFiles={uploads.add}
          fileInput={fileInput}
          transcript={transcript}
          host={host}
          chips={
            <SourceChips
              chips={chips.chips}
              chatOnly={chatMode === 'chat_only'}
              removing={chips.removing}
              failed={chips.failed}
              busy={chips.loading}
              onRemove={key => void chips.remove(key)}
            />
          }
          attachments={
            <AttachmentList
              uploads={uploads.items}
              attachments={fileStatus.files}
              removingId={removingFileId}
              canRetry={uploads.canRetry}
              onCancel={uploads.cancel}
              onRetry={uploads.retry}
              onRemoveUpload={uploads.remove}
              onReattach={id => {
                uploads.remove(id);
                fileInput.current?.click();
              }}
              onRemoveAttachment={fileId => void removeFile(fileId)}
              chatOnly={chatMode === 'chat_only'}
            />
          }
          setup={
            chatMode === 'comparison' && selectedInquiryId ? (
              <ComparisonSetup
                ref={setup}
                choice={choice}
                onChange={setChoice}
                totals={totals}
                quota={quota}
                disabled={busy}
              />
            ) : null
          }
        />
      </div>
      {sourceRequest ? <Suspense fallback={null}>{panel}</Suspense> : null}
      <Suspense fallback={null}>
        {sourcesMounted && selectedInquiryId ? (
          <SourceSelectionDialog
            // A dialog per Q&A: nothing of another Q&A's selection carries over.
            key={selectedInquiryId}
            inquiryId={selectedInquiryId}
            open={sourcesOpen}
            onClose={() => {
              setSourcesOpen(false);
              // The composer shows the saved selection.
              reload();
            }}
          />
        ) : null}
      </Suspense>
      {promptMounted ? (
        <Suspense fallback={null}>
          <PromptDialog
            open={promptOpen}
            inquiryId={selectedInquiryId}
            saved={current?.default_prompt}
            ready={!!current}
            loadFailed={inquiryFailed}
            onSaved={prompt =>
              setInquiry((previous: any) =>
                previous ? { ...previous, default_prompt: prompt } : previous,
              )
            }
            onClose={() => setPromptOpen(false)}
          />
        </Suspense>
      ) : null}
      <ChatWindowModals
        uploadFailedOpen={fileStatus.uploadFailedOpen}
        closeUploadFailed={fileStatus.closeUploadFailed}
      />
      <CopyFallbackDialog text={copyText} onClose={() => setCopyText(null)} />
      {contextHolder}
    </div>
  );
};

export default ChatWindow;
